/**
 * @store/compare — Compare-list of property ids.
 *
 * Foundation for the P3 "Compare" backlog feature. Pure client-side,
 * persisted to localStorage so the comparison survives reload. Capped at
 * 4 entries (industry-standard for property compare UIs).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

const MAX_COMPARE = 4;

export const useCompareStore = create()(
  persist(
    (set, get) => ({
      /** Array of property ids in the compare set. */
      ids: [],

      /** Add an id. Returns true if added, false if list is full or duplicate. */
      add: (id) => {
        if (!id) return false;
        const current = get().ids;
        if (current.includes(id)) return false;
        if (current.length >= MAX_COMPARE) return false;
        set({ ids: [...current, id] });
        return true;
      },

      /** Remove an id. */
      remove: (id) => set({ ids: get().ids.filter((x) => x !== id) }),

      /** Toggle membership. */
      toggle: (id) => {
        const current = get().ids;
        if (current.includes(id)) {
          set({ ids: current.filter((x) => x !== id) });
        } else if (current.length < MAX_COMPARE) {
          set({ ids: [...current, id] });
        }
      },

      /** Empty the compare list. */
      clear: () => set({ ids: [] }),

      /** Is the list at the cap? */
      isFull: () => get().ids.length >= MAX_COMPARE,
    }),
    { name: "he-compare", storage: createJSONStorage(() => localStorage), version: 1 }
  )
);

/* Selectors */
export const useCompareIds = () => useCompareStore((s) => s.ids);
export const useCompareCount = () => useCompareStore((s) => s.ids.length);
export const useIsCompared = (id) => useCompareStore((s) => s.ids.includes(id));

export const COMPARE_MAX = MAX_COMPARE;
